import { forwardRef, memo, type Ref, useMemo, useRef } from 'react';
import { Row, Text } from '~/commonComponents';
import { FormDropdown } from '~/commonComponents/FormDropdown';
import { DropdownOption } from '~/commonComponents/FormDropdown/DropdownOption';
import { useAverageRate } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';
import { useScrollIntoView } from '~/hooks/useScrollIntoView';
import greenCircleCheck from '~/images/green-circle-check.svg';
import warningAlertRed from '~/images/warning_alert.svg';
import warningAlertOrange from '~/images/warning_alert_orange.svg';
import { Colors } from '~/themes/colors';

interface IProfessionalPayFieldProps {
  value?: number | null;
  onChange?: (value: number) => void;
  isOpen: boolean;
  onToggle: () => void;
  professionType: string;
  officeId: string;
}

const getRateStatus = (rate: number, averageRate: number) => {
  const percentage = (rate / averageRate) * 100;

  if (percentage >= 106) {
    return {
      status: 'More likely to fill.',
      message: 'Rate is competitive with the industry average',
      color: Colors.success_500,
      icon: greenCircleCheck,
    };
  }
  if (percentage >= 100) {
    return {
      status: 'Likely to fill.',
      message: 'Based on statewide compensation data.',
      color: Colors.success_500,
      icon: greenCircleCheck,
    };
  }
  if (percentage >= 85) {
    return {
      status: 'Less likely to fill.',
      message: 'Rate is below industry average',
      color: Colors.warning_700,
      icon: warningAlertOrange,
    };
  }
  return {
    status: 'Unlikely to fill.',
    message: 'Rate is well below industry average',
    color: Colors.error_500,
    icon: warningAlertRed,
  };
};

const Component = (
  { value, onChange, isOpen, onToggle, professionType, officeId }: IProfessionalPayFieldProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { data: averageRating } = useAverageRate(officeId);
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  useScrollIntoView(scrollTargetRef, isOpen);

  const professionTypeCode = UserProfession.getCode(professionType) as string | undefined;
  const profRate = (professionTypeCode && averageRating?.[professionTypeCode]) || {
    average: 49,
    min: 18,
    max: 100,
  };

  const averageRate = Math.round(profRate.average);

  const rates = useMemo(() => {
    const result: number[] = [];
    for (let i = profRate.min; i <= profRate.max; i++) {
      result.push(i);
    }
    return result;
  }, [profRate.min, profRate.max]);

  const displayValue = value != null ? `${value}.00 /hour` : undefined;

  const rateStatus = value != null ? getRateStatus(value, averageRate) : null;

  const rateStatusFooter = rateStatus ? (
    <Row
      alignItems="flex-start"
      gap={6}
      data-testid="rate-status-message"
      data-cy="rate-status-message"
      marginTop={12}
      paddingLeft={20}
    >
      <img src={rateStatus.icon} alt="rate-status" data-testid="rate-status-icon" data-cy="rate-status-icon" />
      <Text fontSize={16} color="neutral_500">
        <Text bold fontSize={16} style={{ color: rateStatus.color }}>
          {rateStatus.status}
        </Text>{' '}
        {rateStatus.message}
      </Text>
    </Row>
  ) : undefined;

  return (
    <div ref={ref}>
      <FormDropdown
        label="Professional Pay"
        value={displayValue}
        placeholder="--.-- /hour"
        isOpen={isOpen}
        onToggle={onToggle}
        testId="professional-pay-field"
        prefix={
          <Text bold fontSize={24} color="neutral_600">
            $
          </Text>
        }
        footer={rateStatusFooter}
      >
        {rates.map((rate) => {
          const isSelected = value === rate;
          const isAverage = rate === averageRate;
          const optionLabel = isAverage ? `${rate}.00 /hour - Average` : `${rate}.00 /hour`;

          const shouldScrollTo = isSelected || (value == null && isAverage);

          return (
            <div key={rate} ref={shouldScrollTo ? scrollTargetRef : undefined}>
              <DropdownOption
                isSelected={isSelected}
                onSelect={() => {
                  onChange?.(rate);
                  onToggle();
                }}
                testId={`rate-option-${rate}`}
                bold={isAverage}
              >
                {optionLabel}
              </DropdownOption>
            </div>
          );
        })}
      </FormDropdown>
    </div>
  );
};

export const ProfessionalPayField = memo(forwardRef(Component));
